import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Table,
  Form,
  InputGroup,
  Button,
  ListGroup,
  Badge,
  Nav,
} from "react-bootstrap";
import KnockoutBracket from "./KnockoutBracket"; // Import the new component

const API_URL = process.env.REACT_APP_API_URL;

const TournamentSchedule = () => {
  const { uuid } = useParams();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [view, setView] = useState('groups'); // 'groups' or 'knockout'

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tourneyRes, scheduleRes, playersRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/register/${uuid}`),
        axios.get(`${API_URL}/tournaments/${uuid}/schedule`),
        axios.get(`${API_URL}/playerspublic?limit=all`),
      ]);

      setTournament(tourneyRes.data);
      setMatches(scheduleRes.data);
      setPlayers(new Map(playersRes.data.map((p) => [p.id, p])));
    } catch (err) {
      console.error("Error fetching tournament schedule:", err);
      setError("Không thể tải dữ liệu lịch thi đấu. Giải đấu có thể chưa được bốc thăm hoặc không tồn tại.");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPlayerName = useCallback((id) => players.get(id)?.name || `VĐV ID: ${id}`, [players]);

  // Process matches into groups
  const processedData = useMemo(() => {
    if (matches.length === 0) return { groups: {}, groupNames: [] };
    const groups = matches.reduce((acc, match) => {
      const groupName = match.round_name;
      if (!acc[groupName]) {
        acc[groupName] = {
          players: new Set(),
          matches: [],
        };
      }
      acc[groupName].matches.push(match);
      if (match.player_1_id !== 0) acc[groupName].players.add(match.player_1_id);
      if (match.player_2_id !== 0) acc[groupName].players.add(match.player_2_id);
      if (match.player_3_id !== 0) acc[groupName].players.add(match.player_3_id);
      if (match.player_4_id !== 0) acc[groupName].players.add(match.player_4_id);
      return acc;
    }, {});
    const groupNames = Object.keys(groups).sort();
    return { groups, groupNames };
  }, [matches]);

  // Định nghĩa các vòng knockout theo thứ tự
  const KNOCKOUT_ROUND_ORDER = ['1/32', '1/16', '1/8', 'Tứ kết', 'Bán kết', 'Chung kết'];

  const knockoutData = useMemo(() => {
    const { groupNames } = processedData;

    // Lọc các trận knockout thực từ matches (trim để đảm bảo match đúng)
    const knockoutMatches = matches.filter(m => KNOCKOUT_ROUND_ORDER.includes(m.round_name?.trim()));

    // Helper function để tạo tên đội từ trận đấu
    const getTeamName = (match, isTeam1) => {
      if (isTeam1) {
        const p1 = getPlayerName(match.player_1_id);
        const p2 = match.player_2_id !== 0 ? getPlayerName(match.player_2_id) : null;
        return p2 ? `${p1} & ${p2}` : p1;
      } else {
        const p3 = getPlayerName(match.player_3_id);
        const p4 = match.player_4_id !== 0 ? getPlayerName(match.player_4_id) : null;
        return p4 ? `${p3} & ${p4}` : p3;
      }
    };

    // Helper để lấy điểm từ kết quả
    const getScore = (match, isTeam1) => {
      if (!match.result || match.result === '-:-') return undefined;
      const parts = match.result.split(':');
      if (parts.length !== 2) return undefined;
      return isTeam1 ? parseInt(parts[0]) : parseInt(parts[1]);
    };

    // Nếu có trận knockout thực, sử dụng dữ liệu thực
    if (knockoutMatches.length > 0) {
      console.log('=== KNOCKOUT DEBUG ===');
      console.log('knockoutMatches:', knockoutMatches.map(m => ({ id: m.id, round_name: m.round_name })));

      // Nhóm các trận theo round_name (normalize bằng trim)
      const matchesByRound = {};
      for (const roundName of KNOCKOUT_ROUND_ORDER) {
        matchesByRound[roundName] = knockoutMatches.filter(m => m.round_name?.trim() === roundName);
      }

      console.log('matchesByRound:', Object.entries(matchesByRound).map(([k, v]) => `${k}: ${v.length} trận`));

      // Tìm vòng đầu tiên có trận (vòng knockout xa nhất)
      let firstRoundIndex = KNOCKOUT_ROUND_ORDER.findIndex(r => matchesByRound[r].length > 0);
      if (firstRoundIndex === -1) firstRoundIndex = 0;

      // Tạo seeds cho từng vòng từ dữ liệu thực
      const createSeedsFromMatches = (roundMatches) => {
        return roundMatches.map(match => ({
          matchId: match.id,
          teams: [
            {
              name: getTeamName(match, true),
              score: getScore(match, true)
            },
            {
              name: getTeamName(match, false),
              score: getScore(match, false)
            }
          ]
        }));
      };

      // Tạo cấu trúc rounds cho bracket (không bao gồm chung kết)
      const allRounds = [];
      for (let i = firstRoundIndex; i < KNOCKOUT_ROUND_ORDER.length; i++) {
        const roundName = KNOCKOUT_ROUND_ORDER[i];
        const roundMatches = matchesByRound[roundName];

        if (roundName === 'Chung kết') continue; // Xử lý riêng

        if (roundMatches.length > 0) {
          allRounds.push({
            title: roundName === '1/8' ? 'Vòng 1/8' :
              roundName === '1/16' ? 'Vòng 1/16' :
                roundName === '1/32' ? 'Vòng 1/32' : roundName,
            seeds: createSeedsFromMatches(roundMatches)
          });
        }
      }

      // Chia bracket thành trái/phải
      const leftRounds = [];
      const rightRounds = [];

      for (const round of allRounds) {
        const halfLength = Math.ceil(round.seeds.length / 2);
        leftRounds.push({
          title: round.title,
          seeds: round.seeds.slice(0, halfLength)
        });
        rightRounds.push({
          title: round.title,
          seeds: round.seeds.slice(halfLength)
        });
      }

      // Trận chung kết
      const finalMatches = matchesByRound['Chung kết'];
      console.log('finalMatches:', finalMatches);
      console.log('allRounds:', allRounds.map(r => ({ title: r.title, numSeeds: r.seeds.length })));
      console.log('leftRounds:', leftRounds.map(r => ({ title: r.title, numSeeds: r.seeds.length })));
      console.log('rightRounds:', rightRounds.map(r => ({ title: r.title, numSeeds: r.seeds.length })));

      const finalMatch = {
        title: 'Chung kết',
        seeds: finalMatches.length > 0
          ? createSeedsFromMatches(finalMatches)
          : [{ teams: [{ name: 'TBD' }, { name: 'TBD' }] }]
      };

      // Chỉ trả về nếu có ít nhất 1 vòng
      if (leftRounds.length > 0 || rightRounds.length > 0) {
        const result = {
          left: leftRounds,
          right: rightRounds.filter(r => r.seeds.length > 0),
          final: finalMatch
        };
        console.log('knockoutData result:', JSON.stringify(result, null, 2));
        return result;
      }

      // Trường hợp chỉ có chung kết (không có vòng trước đó)
      if (finalMatches.length > 0) {
        return {
          left: [],
          right: [],
          final: finalMatch
        };
      }
    }

    // FALLBACK: Logic demo dựa trên số bảng (giữ nguyên logic cũ)
    if (groupNames.length < 2) return null;

    const numGroups = groupNames.length;
    let numTeams = numGroups * 2;

    // Ensure numTeams is a power of 2 for a balanced bracket
    if (numTeams < 4 || (numTeams & (numTeams - 1)) !== 0) {
      return null;
    }

    // --- Seeding Logic ---
    const firstHalf = [];
    const secondHalf = [];

    for (let i = 0; i < numGroups; i += 2) {
      if (i + 1 < numGroups) {
        const groupA = groupNames[i];
        const groupB = groupNames[i + 1];
        firstHalf.push({ teams: [{ name: `Nhất ${groupA}` }, { name: `Nhì ${groupB}` }] });
        secondHalf.push({ teams: [{ name: `Nhất ${groupB}` }, { name: `Nhì ${groupA}` }] });
      }
    }
    const initialSeeds = [...firstHalf, ...secondHalf.reverse()];

    const half = Math.ceil(initialSeeds.length / 2);
    const leftSeeds = initialSeeds.slice(0, half);
    const rightSeeds = initialSeeds.slice(half);

    const generateRounds = (seeds) => {
      const rounds = [];
      if (!seeds || seeds.length === 0) return rounds;

      let currentSeeds = seeds;
      while (currentSeeds.length > 0) {
        const numMatches = currentSeeds.length;
        let title;

        if (numMatches === 1) {
          title = 'Bán kết';
        } else if (numMatches === 2) {
          title = 'Tứ kết';
        } else if (numMatches === 4) {
          title = 'Vòng 1/8';
        } else if (numMatches === 8) {
          title = 'Vòng 1/16';
        } else {
          title = `Vòng ${numMatches * 2} đội`;
        }

        rounds.push({ title: title, seeds: currentSeeds });

        if (numMatches === 1) {
          break;
        }

        const nextSeeds = [];
        for (let i = 0; i < currentSeeds.length; i += 2) {
          nextSeeds.push({ teams: [{ name: 'TBD' }, { name: 'TBD' }] });
        }
        currentSeeds = nextSeeds;
      }
      return rounds;
    };

    const finalMatch = {
      title: 'Chung kết',
      seeds: [{ teams: [{ name: 'TBD' }, { name: 'TBD' }] }]
    };

    return {
      left: generateRounds(leftSeeds),
      right: generateRounds(rightSeeds),
      final: finalMatch,
    };

  }, [processedData, matches, getPlayerName]);


  // Filter and search data (for group view)
  const filteredAndSearchedData = useMemo(() => {
    const { groups } = processedData;
    if (Object.keys(groups).length === 0) return [];

    let playerIdsFound = new Set();
    if (searchTerm) {
      for (const [id, player] of players.entries()) {
        if (player.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          playerIdsFound.add(id);
        }
      }
    }

    return Object.entries(groups)
      .map(([groupName, data]) => {
        const groupContainsSearchedPlayer = [...data.players].some(pId => playerIdsFound.has(pId));
        if (searchTerm && !groupContainsSearchedPlayer) return null;
        if (selectedGroup !== "all" && groupName !== selectedGroup) return null;
        return {
          groupName,
          players: [...data.players].map(pId => players.get(pId)).filter(Boolean),
          matches: data.matches,
          isSearched: groupContainsSearchedPlayer,
        };
      })
      .filter(Boolean);
  }, [processedData, searchTerm, selectedGroup, players]);

  // ... (print functions are unchanged)
  // FAIR SCHEDULING CHỈ CHO PRINT
  const createFairScheduleForPrint = useCallback((groupMatches) => {
    if (!groupMatches || groupMatches.length === 0) return [];

    const teamLastMatch = new Map();
    const availableMatches = [...groupMatches];
    const fairSchedule = [];

    // Tạo team IDs
    groupMatches.forEach(match => {
      const team1Id = [match.player_1_id, match.player_2_id].filter(id => id !== 0).sort().join('-');
      const team2Id = [match.player_3_id, match.player_4_id].filter(id => id !== 0).sort().join('-');
      teamLastMatch.set(team1Id, -3);
      teamLastMatch.set(team2Id, -3);
    });

    // Greedy scheduling
    while (availableMatches.length > 0) {
      let bestMatch = null;
      let bestScore = -Infinity;
      let bestIndex = -1;

      for (let i = 0; i < availableMatches.length; i++) {
        const match = availableMatches[i];
        const team1Id = [match.player_1_id, match.player_2_id].filter(id => id !== 0).sort().join('-');
        const team2Id = [match.player_3_id, match.player_4_id].filter(id => id !== 0).sort().join('-');

        const team1Last = teamLastMatch.get(team1Id) || -3;
        const team2Last = teamLastMatch.get(team2Id) || -3;
        const currentIndex = fairSchedule.length;
        const minGap = Math.min(currentIndex - team1Last, currentIndex - team2Last);

        let score = minGap;
        if (minGap < 2) score -= 100;

        if (score > bestScore) {
          bestScore = score;
          bestMatch = match;
          bestIndex = i;
        }
      }

      if (bestMatch) {
        const team1Id = [bestMatch.player_1_id, bestMatch.player_2_id].filter(id => id !== 0).sort().join('-');
        const team2Id = [bestMatch.player_3_id, bestMatch.player_4_id].filter(id => id !== 0).sort().join('-');

        fairSchedule.push(bestMatch);
        teamLastMatch.set(team1Id, fairSchedule.length - 1);
        teamLastMatch.set(team2Id, fairSchedule.length - 1);
        availableMatches.splice(bestIndex, 1);
      }
    }

    return fairSchedule;
  }, []);

  // PRINT SINGLE GROUP
  const printGroup = useCallback((groupName) => {
    const group = processedData.groups[groupName];
    if (!group) return;

    const fairSchedule = createFairScheduleForPrint(group.matches);
    const playerNames = [...group.players].map(id => getPlayerName(id)).join(', ');

    let tableRows = fairSchedule.map((match, index) => {
      const team1Name = [getPlayerName(match.player_1_id),
      match.player_2_id !== 0 ? getPlayerName(match.player_2_id) : null]
        .filter(Boolean).join(' & ');
      const team2Name = [getPlayerName(match.player_3_id),
      match.player_4_id !== 0 ? getPlayerName(match.player_4_id) : null]
        .filter(Boolean).join(' & ');

      return `
        <tr>
          <td class="order-col">${index + 1}</td>
          <td class="team-col">${team1Name}</td>
          <td class="team-col">${team2Name}</td>
          <td class="sec-col"><div class="result-box">___</div></td>
          <td class="sec-col"><div class="result-box">___</div></td>
          <td class="sec-col"><div class="result-box">___</div></td>
          <td class="sec-col"><div class="result-box">___</div></td>
          <td class="sec-col"><div class="result-box">___</div></td>
          <td class="result-col"><div class="result-box">_____</div></td>
        </tr>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${tournament?.name} - ${groupName}</title>
        <style>
          @page { size: A4 landscape; margin: 5mm; }
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
            font-size: 9pt;
            line-height: 1.1;
            margin: 0;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #28a745;
            padding: 8px 0;
            margin-bottom: 12px;
          }
          h1 {
            color: #34495e;
            font-size: 12pt;
            margin: 0 0 3px 0;
            font-weight: bold;
          }
          .group-header {
            background: #f8f9fa;
            border: 2px solid #28a745;
            padding: 8px;
            margin-bottom: 10px;
          }
          .group-title {
            font-size: 11pt;
            font-weight: bold;
            color: #34495e;
            margin: 0 0 3px 0;
          }
          .players {
            font-size: 8pt;
            color: #666;
            margin: 3px 0;
          }
          .match-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
          }
          .match-table th, .match-table td {
            border: 1px solid #000;
            padding: 4px 2px;
            text-align: center;
            vertical-align: middle;
          }
          .match-table th {
            background: #34495e !important;
            color: white !important;
            font-weight: bold;
            font-size: 7pt;
          }
          .order-col { width: 5%; }
          .team-col {
            width: 17%;
            text-align: left;
            padding-left: 4px;
            font-size: 7pt;
          }
          .sec-col {
            width: 8%;
            background: #fff8dc !important;
            min-height: 20px;
          }
          .result-col {
            width: 12%;
            background: #ffe4b5 !important;
            min-height: 20px;
          }
          .result-box {
            border: 1px solid #000;
            background: white;
            width: 90%;
            height: 18px;
            margin: 1px auto;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8pt;
            font-weight: bold;
          }
          @media print {
            body { -webkit-print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        <div class="header">
          <h1>${tournament?.name || 'LỊCH THI ĐẤU'}</h1>
          <p style="font-size: 9pt; margin: 3px 0; color: #666;">
            ${groupName}
          </p>
        </div>

        <div class="group-header">
          <div class="group-title">${groupName}</div>
          <div class="players">
            <strong>VĐV (${group.players.size}):</strong> ${playerNames}
          </div>
          <div style="font-size: 7pt; color: #666; margin-top: 3px;">
            ${fairSchedule.length} trận
          </div>
        </div>

        <table class="match-table">
          <thead>
            <tr>
              <th class="order-col">#</th>
              <th class="team-col">ĐỘI 1</th>
              <th class="team-col">ĐỘI 2</th>
              <th class="sec-col">S1</th>
              <th class="sec-col">S2</th>
              <th class="sec-col">S3</th>
              <th class="sec-col">S4</th>
              <th class="sec-col">S5</th>
              <th class="result-col">K.QUẢ</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }, [tournament, processedData, createFairScheduleForPrint, getPlayerName]);

  // PRINT ALL GROUPS
  const printAll = useCallback(() => {
    const allGroups = processedData.groups;

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${tournament?.name || 'Lịch thi đấu'}</title>
        <style>
          @page { size: A4 landscape; margin: 5mm; }
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
            font-size: 8pt;
            line-height: 1.1;
            color: #000;
            margin: 0;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #34495e;
            padding: 8px 0;
            margin-bottom: 12px;
          }
          h1 {
            color: #34495e;
            font-size: 12pt;
            margin: 0 0 3px 0;
            font-weight: bold;
          }
          .group {
            margin: 20px 0;
            page-break-inside: avoid;
          }
          .group-header {
            background: #f8f9fa;
            border: 2px solid #34495e;
            padding: 8px;
            margin-bottom: 8px;
          }
          .group-title {
            font-size: 10pt;
            font-weight: bold;
            color: #34495e;
            margin: 0 0 3px 0;
          }
          .players {
            font-size: 7pt;
            color: #666;
            margin: 2px 0;
          }
          .match-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }
          .match-table th, .match-table td {
            border: 1px solid #000;
            padding: 3px 1px;
            text-align: center;
            vertical-align: middle;
          }
          .match-table th {
            background: #34495e !important;
            color: white !important;
            font-weight: bold;
            font-size: 6pt;
          }
          .order-col { width: 4%; }
          .team-col {
            width: 16%;
            text-align: left;
            padding-left: 3px;
            font-size: 6pt;
          }
          .sec-col {
            width: 7%;
            background: #fff8dc !important;
            min-height: 16px;
          }
          .result-col {
            width: 10%;
            background: #ffe4b5 !important;
            min-height: 16px;
          }
          .result-box {
            border: 1px solid #000;
            background: white;
            width: 85%;
            height: 14px;
            margin: 1px auto;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 7pt;
            font-weight: bold;
          }
          @media print {
            body { -webkit-print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body onload="window.print()">
    `;

    html += `
        <div class="header">
          <h1>${tournament?.name || 'LỊCH THI ĐẤU'}</h1>
          <p style="font-size: 8pt; margin: 2px 0; color: #666;">
            In: ${new Date().toLocaleString('vi-VN')}
          </p>
        </div>
    `;

    Object.entries(allGroups).forEach(([groupName, group]) => {
      const fairSchedule = createFairScheduleForPrint(group.matches);
      const playerNames = [...group.players].map(id => getPlayerName(id)).join(', ');

      let tableRows = fairSchedule.map((match, index) => {
        const team1Name = [getPlayerName(match.player_1_id),
        match.player_2_id !== 0 ? getPlayerName(match.player_2_id) : null]
          .filter(Boolean).join(' & ');
        const team2Name = [getPlayerName(match.player_3_id),
        match.player_4_id !== 0 ? getPlayerName(match.player_4_id) : null]
          .filter(Boolean).join(' & ');

        return `
          <tr>
            <td class="order-col">${index + 1}</td>
            <td class="team-col">${team1Name}</td>
            <td class="team-col">${team2Name}</td>
            <td class="sec-col"><div class="result-box">___</div></td>
            <td class="sec-col"><div class="result-box">___</div></td>
            <td class="sec-col"><div class="result-box">___</div></td>
            <td class="sec-col"><div class="result-box">___</div></td>
            <td class="sec-col"><div class="result-box">___</div></td>
            <td class="result-col"><div class="result-box">_____</div></td>
          </tr>
        `;
      }).join('');

      html += `
        <div class="group">
          <div class="group-header">
            <div class="group-title">${groupName}</div>
            <div class="players">
              <strong>VĐV (${group.players.size}):</strong> ${playerNames}
            </div>
            <div style="font-size: 6pt; color: #666; margin-top: 2px;">
              ${fairSchedule.length} trận
            </div>
          </div>

          <table class="match-table">
            <thead>
              <tr>
                <th class="order-col">#</th>
                <th class="team-col">ĐỘI 1</th>
                <th class="team-col">ĐỘI 2</th>
                <th class="sec-col">S1</th>
                <th class="sec-col">S2</th>
                <th class="sec-col">S3</th>
                <th class="sec-col">S4</th>
                <th class="sec-col">S5</th>
                <th class="result-col">K.Q</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      `;
    });

    html += `
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }, [tournament, processedData, createFairScheduleForPrint, getPlayerName]);


  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3 text-primary" />
          <p className="fs-5 text-muted fw-medium">Đang tải lịch thi đấu...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger" className="mx-auto" style={{ maxWidth: '500px' }}>
          <div className="d-flex align-items-center">
            <div>
              <Alert.Heading className="mb-2">Lỗi tải dữ liệu</Alert.Heading>
              <p className="mb-3">{error}</p>
              <Button variant="outline-danger" size="sm" onClick={fetchData} className="rounded-pill px-4">
                Thử lại
              </Button>
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="text-start mb-5 px-3">
        <h1 className="fw-bold mb-1">{tournament?.name}</h1>
        <p className="text-muted">Thông tin chi tiết và lịch thi đấu chính thức</p>
      </div>

      <Nav variant="tabs" activeKey={view} onSelect={(k) => setView(k)} className="mb-5 custom-tabs-minimal justify-content-start">
        <Nav.Item>
          <Nav.Link eventKey="groups">Vòng Bảng</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="knockout" disabled={!knockoutData}>Vòng Loại Trực Tiếp</Nav.Link>
        </Nav.Item>
      </Nav>

      {view === 'groups' ? (
        <>
          {/* Search and Filter Controls */}
          <Row className="mb-5 justify-content-between align-items-end gap-3">
            <Col md={6} sm={12}>
              <InputGroup className="shadow-sm rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <Form.Control
                  type="search"
                  placeholder="Tìm kiếm VĐV theo tên..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-0"
                  style={{ backgroundColor: 'transparent' }}
                />
                {searchTerm && <Button variant="outline-secondary" onClick={() => setSearchTerm("")} className="border-0" style={{ backgroundColor: 'transparent' }}>✕</Button>}
              </InputGroup>
            </Col>
            <Col md={5} sm={12}>
              <Form.Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} className="shadow-sm rounded border-0" style={{ backgroundColor: '#f8f9fa', fontWeight: '500', borderRadius: '8px' }}>
                <option value="all">Tất cả các bảng</option>
                {processedData.groupNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </Form.Select>
            </Col>
          </Row>

          {/* Main Content */}
          {filteredAndSearchedData.length > 0 ? (
            filteredAndSearchedData.map(({ groupName, players: groupPlayers, matches: groupMatches, isSearched }) => (
              <Card key={groupName} className="mb-5 border-0 shadow-sm animate-fade" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <Card.Header className="bg-white py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 border-bottom">
                  <div>
                    <span className="text-muted small text-uppercase fw-bold d-block mb-1">Bảng Đấu</span>
                    <h3 className="fw-bold mb-0">{groupName}</h3>
                    {isSearched && <span className="text-warning small fw-bold">Tìm thấy kết quả</span>}
                  </div>
                  <Button variant="outline-dark" size="sm" onClick={() => printGroup(groupName)} className="rounded-pill px-4">
                    In bảng đấu
                  </Button>
                </Card.Header>

                <Card.Body className="p-0">
                  {/* Players List Section */}
                  <div className="p-4 border-bottom">
                    <h5 className="mb-3 fw-bold">
                      Các VĐV trong bảng ({groupPlayers.length})
                    </h5>
                    <ListGroup horizontal className="flex-wrap" style={{ gap: '0.75rem' }}>
                      {groupPlayers.map(p => (
                        <ListGroup.Item
                          key={p.id}
                          className="border-0 bg-white rounded-3 px-3 py-2 shadow-sm text-decoration-none text-dark d-flex align-items-center"
                          style={{
                            transition: 'all 0.2s ease',
                            minWidth: '140px'
                          }}
                        >
                          <Link
                            to={`/players/${p.id}`}
                            className="text-dark fw-medium text-truncate"
                            style={{ maxWidth: '120px' }}
                          >
                            {p.name}
                          </Link>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>

                  {/* Matches Schedule Section */}
                  <div className="p-4">
                    <h5 className="mb-4 fw-bold">
                      Lịch thi đấu ({groupMatches.length})
                    </h5>
                    <div className="table-responsive" style={{ border: 'none' }}>
                      <Table hover className="mb-0">
                        <thead className="bg-white border-bottom">
                          <tr>
                            <th className="py-3 text-muted small text-uppercase fw-bold">Đội 1</th>
                            <th className="text-center py-3 text-muted small text-uppercase fw-bold">VS</th>
                            <th className="py-3 text-muted small text-uppercase fw-bold">Đội 2</th>
                            <th className="text-center py-3 text-muted small text-uppercase fw-bold">Kết quả</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupMatches.map((match) => {
                            const team1Players = [getPlayerName(match.player_1_id), match.player_2_id !== 0 ? getPlayerName(match.player_2_id) : null].filter(Boolean).join(' & ');
                            const team2Players = [getPlayerName(match.player_3_id), match.player_4_id !== 0 ? getPlayerName(match.player_4_id) : null].filter(Boolean).join(' & ');
                            return (
                              <tr key={match.id}>
                                <td className="py-3 fw-medium text-start">{team1Players}</td>
                                <td className="text-center py-3 align-middle">VS</td>
                                <td className="py-3 fw-medium text-start">{team2Players}</td>
                                <td className="text-center py-3 align-middle">
                                  <span className={`fw-bold fs-6 ${match.result !== '-:-' ? 'text-dark' : 'text-muted'}`}>
                                    {match.result}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))
          ) : (
            <Alert variant="info" className="text-center p-5">Không có dữ liệu để hiển thị.</Alert>
          )}

          {/* Print All Button */}
          {filteredAndSearchedData.length > 0 && (
            <div className="text-center mt-5 pt-4">
              <Button variant="dark" size="lg" onClick={printAll} className="rounded-pill px-5 py-3">
                In toàn bộ lịch ({matches.length} trận)
              </Button>
            </div>
          )}
        </>
      ) : (
        knockoutData && <KnockoutBracket left={knockoutData.left} right={knockoutData.right} final={knockoutData.final} />
      )}
    </Container>
  );
};

export default TournamentSchedule;
